/* ==========================================================================
   KAN — shared scroll-motion core.

   WHY THIS EXISTS
   The original standalone page captured its element references once, at parse
   time, into module-scoped vars (`var morphBottle = $('#morphBottle')`). That
   is fine for a static file, but the Shopify Theme Editor re-renders one
   section at a time over AJAX: the section's DOM is replaced wholesale and
   every captured reference goes stale, so the animation silently dies the
   moment a merchant edits it. Editors also let a section be added twice, which
   unique IDs cannot survive.

   So sections here never hold global refs. Each one registers an initialiser
   keyed by name; the core mounts it per root element, re-mounts on
   `shopify:section:load`, and tears down on `shopify:section:unload`.

   Every section shares ONE requestAnimationFrame ticker. Adding sections costs
   no extra frames.
   ========================================================================== */

window.KanMotion = window.KanMotion || (function () {
  'use strict';

  /* ---- math helpers (ported verbatim from the source page) -------------- */
  var clamp = function (x) { return Math.min(1, Math.max(0, x)); };
  var lerp = function (a, b, t) { return a + (b - a) * t; };
  var easeIO = function (t) { return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; };
  var easeOut = function (t) { return 1 - Math.pow(1 - t, 3); };
  var seg = function (p, a, b) { return clamp((p - a) / (b - a)); };

  var toArray = function (list) { return Array.prototype.slice.call(list || []); };

  /* ---- reduced motion, kept live -----------------------------------------
     The source read this once at load. Reading it live means a visitor who
     flips the OS setting mid-session gets the static layout without a reload,
     and the Theme Editor's own motion preview behaves correctly. */
  var reduceQuery = window.matchMedia('(prefers-reduced-motion:reduce)');
  var reduced = function () { return reduceQuery.matches; };

  /* ---- writing direction -------------------------------------------------
     The horizontal gallery's travel depends on it: under `dir=rtl` a flex row
     lays panels right-to-left and the surplus track overflows to the LEFT, so
     sliding it POSITIVE walks panels into view. Under LTR the sign flips.
     Hardcoding +1 (as the source did) would silently reverse the gallery the
     day an English locale is switched on. */
  var direction = function () {
    return getComputedStyle(document.documentElement).direction === 'rtl' ? 1 : -1;
  };

  /* ---- scroll container ---------------------------------------------------
     THE bug that froze every scroll animation on desktop.

     Horizon does not scroll the window at >=990px. assets/base.css sets
     `html, body { height:100dvh; overflow:hidden }` and scrolls `.page-wrapper`
     instead. Two consequences, both fatal to the original wiring:

       - window.scrollY is permanently 0
       - scroll events fire on .page-wrapper and do NOT bubble to window, so
         window's 'scroll' listener never ran and update() was only ever called
         on resize/load

     getBoundingClientRect() stays viewport-relative and correct either way,
     which is why pinProgress/scrubProgress needed no change — they simply were
     never being called.

     The scroller is detected from computed overflow rather than by re-testing
     Horizon's 990px breakpoint, so a change to that breakpoint cannot silently
     desynchronise us. Horizon exposes the same idea from assets/scroll-container.js,
     but that is an ES module and this is a classic script. */
  var PAGE_WRAPPER = '.page-wrapper';

  function scrollElement() {
    var pw = document.querySelector(PAGE_WRAPPER);
    if (pw) {
      var overflowY = getComputedStyle(pw).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') return pw;
    }
    return document.scrollingElement || document.documentElement;
  }

  function scrollY() {
    return scrollElement().scrollTop;
  }

  /* Total scrollable distance, for the progress bar. */
  function scrollMax() {
    var el = scrollElement();
    return Math.max(1, el.scrollHeight - el.clientHeight);
  }

  /* The breakpoint swaps the scroller, so the listener is re-bound on resize. */
  var boundTarget = null;

  function bindScroll() {
    var el = scrollElement();
    var target =
      el === document.scrollingElement || el === document.documentElement ? window : el;
    if (target === boundTarget) return;
    if (boundTarget) boundTarget.removeEventListener('scroll', request);
    target.addEventListener('scroll', request, { passive: true });
    boundTarget = target;
  }

  /* ---- progress readers -------------------------------------------------- */

  /* 0 when a pinned stage hits the top of the viewport, 1 when it releases */
  function pinProgress(stage) {
    var total = stage.offsetHeight - window.innerHeight;
    if (total <= 0) return 0;
    return clamp(-stage.getBoundingClientRect().top / total);
  }

  /* generic scrub: element top travels from a*vh (p=0) to b*vh (p=1) */
  function scrubProgress(el, a, b) {
    a = a === undefined ? 0.92 : a;
    b = b === undefined ? 0.45 : b;
    var top = el.getBoundingClientRect().top;
    return clamp((a * window.innerHeight - top) / ((a - b) * window.innerHeight));
  }

  /* ---- registry ---------------------------------------------------------- */
  var initialisers = {};   /* name -> function(root) -> api */
  var entries = [];        /* { root, api } */
  var mounted = new WeakSet();

  function mount(root, name) {
    if (!root || mounted.has(root)) return;
    var init = initialisers[name];
    if (!init) return;              /* section markup present, script not yet run */

    // Mark mounted only after successful initialization.

    var api;
    try {
      api = init(root);
    } catch (err) {
      if (window.console) console.error('[KanMotion] init failed for ' + name, err);
      return;
    }
    if (!api) return;

    mounted.add(root);
    root.dataset.kanReady = "true";
    entries.push({ root: root, api: api });
    if (api.layout) safe(api.layout);
    request();
  }

  function unmountWithin(container) {
    entries = entries.filter(function (entry) {
      var gone = container === entry.root ||
        (container.contains && container.contains(entry.root)) ||
        !document.contains(entry.root);
      if (gone) { mounted.delete(entry.root); if (entry.api.destroy) safe(entry.api.destroy); }
      return !gone;
    });
  }

  function safe(fn) {
    try { fn(); } catch (err) {
      if (window.console) console.error('[KanMotion]', err);
    }
  }

  /* Public: a section declares itself once. Calling it again (which happens
     when the editor re-renders a section whose inline script re-executes) just
     refreshes the initialiser and mounts any roots not yet mounted. */
  function section(name, init) {
    initialisers[name] = init;
    toArray(document.querySelectorAll('[data-kan-section="' + name + '"]'))
      .forEach(function (root) { mount(root, name); });
  }

  /* ---- ticker ------------------------------------------------------------
     Replace the pending frame rather than guarding with a boolean latch: a
     latch stays stuck if the scheduled frame never fires (rAF is throttled
     while the tab is hidden), freezing every scroll animation until the next
     resize. This bug was already reasoned about in the source page — keeping
     the same defence. */
  var rafId = 0;

  function request() {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(function () {
      rafId = 0;
      run();
    });
  }

  function run() {
    var i;
    /* sweep detached roots (editor removals that fired no unload) */
    for (i = entries.length - 1; i >= 0; i--) {
      if (!document.contains(entries[i].root)) {
        if (entries[i].api.destroy) safe(entries[i].api.destroy);
        entries.splice(i, 1);
      }
    }
    for (i = 0; i < entries.length; i++) {
      if (entries[i].api.update) safe(entries[i].api.update);
    }
  }

  function relayout() {
    bindScroll();
    entries.forEach(function (entry) {
      if (entry.api.layout) safe(entry.api.layout);
    });
    request();
  }

  /* ---- wiring ------------------------------------------------------------ */
  bindScroll();
  window.addEventListener('resize', relayout);
  window.addEventListener('orientationchange', relayout);
  window.addEventListener('load', relayout);
  document.addEventListener('shopify:section:reorder', relayout);
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) request();
  });
  if (reduceQuery.addEventListener) {
    reduceQuery.addEventListener('change', relayout);
  }

  /* Theme Editor lifecycle — the whole reason this file exists. */
  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target;
    if (!target || !target.querySelectorAll) return;
    var roots = toArray(target.querySelectorAll('[data-kan-section]'));
    if (target.getAttribute && target.getAttribute('data-kan-section')) roots.push(target);
    roots.forEach(function (root) {
      mount(root, root.getAttribute('data-kan-section'));
    });
    relayout();
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (event.target) unmountWithin(event.target);
  });

  /* Fonts change metrics, which changes the gallery's track width. */
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(relayout).catch(function () {});
  }

  /* ---- deferred registration --------------------------------------------
     This file is loaded with `defer`, so it executes AFTER the inline <script>
     of every section on the page. Sections therefore never call KanMotion
     directly — they push [name, init] onto window.KanInit, which we drain here.

     After the drain, window.KanInit.push runs immediately instead of queueing,
     which is exactly what is needed when the Theme Editor re-renders a section
     and its inline script executes a second time. */
  var publicApi = {
    section: section,
    request: request,
    relayout: relayout,
    clamp: clamp,
    lerp: lerp,
    easeIO: easeIO,
    easeOut: easeOut,
    seg: seg,
    toArray: toArray,
    reduced: reduced,
    direction: direction,
    scrollY: scrollY,
    scrollMax: scrollMax,
    scrollElement: scrollElement,
    pinProgress: pinProgress,
    scrubProgress: scrubProgress
  };

  var queued = window.KanInit || [];
  window.KanInit = {
    push: function (item) {
      section(item[0], function (root) { return item[1](root, publicApi); });
    }
  };
  for (var qi = 0; qi < queued.length; qi++) window.KanInit.push(queued[qi]);

  return publicApi;
})();
